import React, { useState } from 'react';
import Card from '../../components/common/Card/Card';
import ImagePlaceholder from '../../components/common/ImagePlaceholder/ImagePlaceholder';
import Modal from '../../components/common/Modal/Modal';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import { NEWS_DATA, formatNewsDate } from '../../constants/newsData';
import './News.css';

/**
 * Página de Noticias - Colegio Bertolt Brecht
 */
const News = () => {
    const [selectedCategory, setSelectedCategory] = useState('Todas');
    const [selectedNews, setSelectedNews] = useState(null);

    // Obtener categorías únicas
    const categories = ['Todas', ...new Set(NEWS_DATA.map(news => news.category))];

    // Filtrar noticias por categoría
    const filteredNews = selectedCategory === 'Todas'
        ? NEWS_DATA
        : NEWS_DATA.filter(news => news.category === selectedCategory);

    const handleReadMore = (newsItem) => {
        setSelectedNews(newsItem);
    };

    const closeModal = () => {
        setSelectedNews(null);
    };

    return (
        <div className="news">
            {/* Page Header */}
            <PageHeader
                title="Noticias y Eventos"
                subtitle="Manténte informado sobre las últimas novedades de nuestra comunidad educativa"
            />

            <div className="container">
                {/* Filtros por categoría */}
                <section className="news__filters animate-slide-up">
                    <div className="news__filter-buttons">
                        {categories.map((category) => (
                            <button
                                key={category}
                                className={`news__filter-btn ${selectedCategory === category ? 'news__filter-btn--active' : ''}`}
                                onClick={() => setSelectedCategory(category)}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </section>

                {/* Grid de Noticias */}
                <section className="news__grid-section">
                    <div className="news__grid">
                        {filteredNews.map((item, index) => (
                            <Card key={item.id} hoverable className={`animate-scale-in animate-delay-${Math.min(index % 3 + 1, 4)}`}>
                                <article className="news__item">
                                    <div className="news__item-image">
                                        <ImagePlaceholder
                                            src={item.image}
                                            aspectRatio="16/9"
                                            icon="📰"
                                            alt={item.imageAlt || item.title}
                                        />
                                    </div>
                                    <div className="news__item-content">
                                        <span className="news__category">{item.category}</span>
                                        <h2 className="news__title">{item.title}</h2>
                                        <p className="news__date">📅 {formatNewsDate(item.date)}</p>
                                        {item.author && (
                                            <p className="news__author">✍️ {item.author}</p>
                                        )}
                                        <p className="news__excerpt">{item.excerpt}</p>
                                        <button
                                            className="news__read-more"
                                            onClick={() => handleReadMore(item)}
                                        >
                                            Leer más →
                                        </button>
                                    </div>
                                </article>
                            </Card>
                        ))}
                    </div>

                    {filteredNews.length === 0 && (
                        <div className="news__empty">
                            <p>No hay noticias en esta categoría.</p>
                        </div>
                    )}
                </section>
            </div>

            {/* Modal de Detalle de Noticia */}
            <Modal
                isOpen={!!selectedNews}
                onClose={closeModal}
                title={selectedNews?.title || ''}
                size="lg"
            >
                {selectedNews && (
                    <div className="news-modal">
                        <div className="news-modal__image">
                            <ImagePlaceholder
                                src={selectedNews.image}
                                aspectRatio="16/9"
                                icon="📰"
                                alt={selectedNews.imageAlt || selectedNews.title}
                            />
                        </div>
                        <div className="news-modal__meta">
                            <span className="news-modal__category">{selectedNews.category}</span>
                            <span className="news-modal__date">📅 {formatNewsDate(selectedNews.date)}</span>
                            {selectedNews.author && (
                                <span className="news-modal__author">✍️ {selectedNews.author}</span>
                            )}
                        </div>
                        <div className="news-modal__content">
                            {selectedNews.content.split('\n').map((paragraph, index) => (
                                paragraph.trim() && <p key={index}>{paragraph}</p>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default News;
